import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type CloneRecordsApiProps } from '../../@types/records';
import useActiveRecordRef from '../../hooks/use-active-record-ref';
import { moduleApi } from '../../redux/services/module';
import { recordApi } from '../../redux/services/record';
import { type RecordBackgroundJobDetails } from '../../redux/slices/background-job-slice';
import { appDispatch } from '../../redux/store-accessors';
import { constructRecordTitle } from '../record/util/record-component-util';
import { type BackgroundJobPanelItemProps } from './background-job-panel.component';
import BaseJobPanelItem from './base-job-panel-item.component';

// The clone record job item component
function RecordBackgroundJobItem({
  job,
  onRemove,
}: BackgroundJobPanelItemProps<CloneRecordsApiProps, RecordBackgroundJobDetails>) {
  const { t } = useTranslation();
  const { activeRecordReference } = useActiveRecordRef();

  let clonedRecordIds: string[] = [];
  if (job?.details?.clonedRecordIds) {
    clonedRecordIds = (job.details.clonedRecordIds as string).split(',');
  }

  const handleClick = useCallback(() => {
    if (clonedRecordIds.length === 1) {
      const recordDispatch = appDispatch(
        recordApi.endpoints.getRecordById.initiate({ id: clonedRecordIds[0], keyTypeId: false })
      );
      const moduleDispatch = appDispatch(moduleApi.endpoints.modules.initiate({}));
      return Promise.all([recordDispatch.unwrap(), moduleDispatch.unwrap()])
        .then(([recordResponse, modulesResponse]) => {
          const record = recordResponse.original;
          const title = constructRecordTitle(record.formName, record.docNo, record.status, record.title);
          const module = modulesResponse?.find((item) => item.viewId === record.viewId);
          activeRecordReference.current?.addActiveRecord(String(record.id), module?.icon || '', title, undefined, {
            title,
            recordId: record.id,
            viewId: record.viewId,
            moduleIcon: module?.icon,
            formId: record.formId,
            formName: record.formName,
            docNo: record.docNo,
            status: record.status,
            vikingLinkUrl: record.vikingLinkUrl,
            displayText: record.displayText,
            openInEditMode: true,
          });
        })
        .finally(() => {
          recordDispatch.unsubscribe();
          moduleDispatch.unsubscribe();
        });
    }
  }, [job]);

  return (
    <BaseJobPanelItem
      title={job.details?.text ?? job.details?.parentRecordTitle ?? 'Parent record title not available'}
      subtitle={t('clone-records')}
      status={job.status}
      onRemove={onRemove}
      onClick={clonedRecordIds.length === 1 ? handleClick : undefined}
    />
  );
}

export default React.memo(RecordBackgroundJobItem);
